import { useState, useEffect, useMemo } from "react";
import { fetchCompanyNews } from "./finnhubNews";
import { useHebrewNews } from "./headline";
import { isOfficial } from "./geoEvents";
import { isAbout, eventNames } from "./newsMatch";
import { isAdvisoryArticle } from "../constants/advisory";

// ─── What was published about a holding today ───────────────────────────────
//
// Read by the holding panel (and, until sprint 19 moved the articles off the
// home card, by "מה הזיז את התיק"). Two separate facts, kept separate on purpose. The arithmetic
// (which holding moved the portfolio, and by how much) is ours and exact. The
// articles are what named sources published about the company in the last
// 24 hours. They are shown NEXT TO the number, never as its explanation: no
// "because", no "following", no "on the back of". The reader draws the line;
// the app does not draw it for them. That is the legal boundary of this
// screen and every string in these two components respects it.

export const DAY_MS = 24 * 60 * 60 * 1000;

// Official documents (Federal Register rows in geo_events) that name the
// ticker and were published in the window. These go before any article: a
// government document about the company is a different kind of fact from a
// news story about it.
export function officialDocsFor(geoEvents, symbol, windowMs = DAY_MS, name = "") {
  if (!Array.isArray(geoEvents)) return [];
  const since = Date.now() - windowMs;
  return geoEvents.filter(ev =>
    isOfficial(ev)
    && eventNames(ev, symbol, name)
    && new Date(ev.published_at || ev.created_at).getTime() >= since);
}

// "כתבות על החברה" is a claim, so an article must be about the company: the
// shared rule in utils/newsMatch.js · the ticker or the company's first name
// in the HEADLINE, never a press release. Finnhub tags a company feed with the
// symbol it was asked for, so a miss is dropped and "no articles" is the
// honest result when all miss.
export function mentionsCompany(article, symbol, name) {
  return isAbout({ ...article, feed: "company" }, symbol, name);
}

// Articles about each symbol from the last 24 hours, newest first, at most
// `perSymbol` each, with a Hebrew headline when one can be had. Finnhub's
// window is a calendar date, so `days: 1` reaches back into yesterday; the
// 24-hour cut is applied here, on the article's own timestamp.
//
// Returns { [symbol]: null | Article[] } — null while that symbol is still
// loading, so a caller can draw a skeleton for one company and a list for
// the next. Titles land in `titles` keyed by article id, and the original
// English stays on the article. `names` maps symbol → company name for the
// relevance check; a symbol with no known name is matched on the ticker only.
// `days`: how far back (1 by default · "today"). The holding panel asks for 7
// (21/10/2026): a smaller company often has no article in a day, and "אין
// כתבות מהיום" under almost every holding said little.
export function useTodayNews(symbols, { perSymbol = 2, names = {}, days = 1 } = {}) {
  const key = (symbols || []).join(",");
  const nameKey = (symbols || []).map(s => names[s] || "").join("|");
  const [news, setNews]     = useState({});

  useEffect(() => {
    let alive = true;
    const syms = key ? key.split(",") : [];
    setNews(Object.fromEntries(syms.map(s => [s, null])));
    if (!syms.length) return;
    const since = Math.floor((Date.now() - days * DAY_MS) / 1000);
    const nm = Object.fromEntries(syms.map((s, i) => [s, nameKey.split("|")[i] || ""]));
    const load = sym => fetchCompanyNews(sym, { days })
      // Advice-worded headlines are not shown next to a holding
      // (constants/advisory.js).
      .then(list => list.filter(a => a.datetime >= since && mentionsCompany(a, sym, nm[sym]) && !isAdvisoryArticle(a)).slice(0, perSymbol))
      .catch(() => [])
      .then(list => { if (alive) setNews(prev => ({ ...prev, [sym]: list })); });
    syms.forEach(load);
    return () => { alive = false; };
  }, [key, nameKey, perSymbol, days]);

  // The Hebrew headlines (26/10/2026): from the server with the news, or
  // from the cache once the background run has translated them. An article
  // without one is not shown (utils/headline.js, useHebrewNews).
  const all = useMemo(() => Object.values(news).flat().filter(Boolean), [news]);
  const { he, ready } = useHebrewNews(all);
  const titles = useMemo(() => Object.fromEntries(Object.entries(he).map(([k, v]) => [k, v.title])), [he]);

  return { news, titles, ready };
}

export function clockTime(sec) {
  return new Date(sec * 1000).toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" });
}

// "21/10 · 14:05" · an article from another day says which day.
export function dateTime(sec) {
  const d = new Date(sec * 1000);
  const day = d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit" });
  return d.toDateString() === new Date().toDateString() ? clockTime(sec) : `${day} · ${clockTime(sec)}`;
}
